import { useEffect, useRef } from "react";

const TV_INTERVAL = { "5M": "5", "15M": "15", "1H": "60", "4H": "240" };

// Swallow the harmless "querySelector of null" the TradingView embed can throw
// when its container is torn down (dev StrictMode double-mount) — keeps the
// webpack dev overlay from popping over a chart that actually renders fine.
function installTvErrorGuard() {
  if (window.__tvGuard) return;
  const isBenign = (msg = "", src = "") =>
    /querySelector/.test(msg) && /tradingview/.test(src);
  const onError = (e) => {
    if (isBenign(e.message || "", e.filename || "")) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  };
  window.addEventListener("error", onError, true);
  window.__tvGuard = true;
}

/**
 * Live real-time XAU/USD candlestick chart via TradingView's free Advanced Chart
 * widget. Smooth tick-by-tick movement, no API key required.
 */
export function TradingViewChart({ tf = "15M", theme = "dark", height = 380 }) {
  const ref = useRef(null);

  useEffect(() => {
    installTvErrorGuard();
    const container = ref.current;
    if (!container) return;
    container.innerHTML = "";
    const widget = document.createElement("div");
    widget.className = "tradingview-widget-container__widget";
    widget.style.height = "100%";
    widget.style.width = "100%";
    container.appendChild(widget);

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbol: "OANDA:XAUUSD",
      interval: TV_INTERVAL[tf] || "15",
      timezone: "Etc/UTC",
      theme: theme === "light" ? "light" : "dark",
      style: "1",
      locale: "en",
      hide_side_toolbar: true,
      allow_symbol_change: false,
      save_image: false,
      calendar: false,
      backgroundColor: theme === "light" ? "#FFFFFF" : "#0D1117",
      gridColor: "rgba(152,162,179,0.12)",
      autosize: true,
      support_host: "https://www.tradingview.com",
    });
    container.appendChild(script);

    return () => { try { container.innerHTML = ""; } catch { /* ignore */ } };
  }, [tf, theme]);

  return (
    <div className="tradingview-widget-container rounded-lg overflow-hidden"
      ref={ref} style={{ height, width: "100%" }} data-testid="tradingview-chart" />
  );
}
